import {
  useCreateAssignments,
  useDeleteAssignment,
  useReplaceAssignment,
} from "@/hooks/form-builder/useFormBuilderAssignments";
import {
  useAssignmentsAssistantStore,
  type ChangeWithStatus,
  type PageChange,
} from "@/store/form-builder/useAssignmentsAssistantStore";
import { canApplyChange } from "./ProposedChangesCard";
import { toCreatePayload } from "./toCreatePayload";

const isOpen = ({ change }: PageChange) =>
  (change.status === "pending" || change.status === "failed") && canApplyChange(change);

// The status as it is now, not as the caller saw it when it rendered.
function currentChange(entryId: number, key: string): ChangeWithStatus | undefined {
  const entry = useAssignmentsAssistantStore.getState().messages.find((m) => m.id === entryId);
  return entry?.kind === "changes" ? entry.changes.find((c) => c.key === key) : undefined;
}

/** Applies proposed changes with the same requests the page's own controls use. */
export function useApplyAssignmentChanges(tenantId: string | undefined) {
  const setChangeStatus = useAssignmentsAssistantStore((s) => s.setChangeStatus);
  const createAssignments = useCreateAssignments();
  const replaceAssignment = useReplaceAssignment();
  const deleteAssignment = useDeleteAssignment();

  const apply = async (entryId: number, seen: ChangeWithStatus) => {
    const change = currentChange(entryId, seen.key);
    if (!tenantId || !change || !isOpen({ entryId, change })) return;
    setChangeStatus(entryId, change.key, "applying");
    try {
      const payload = toCreatePayload(change, tenantId);
      if (change.action === "remove" && change.rowId) {
        await deleteAssignment.mutateAsync({ assignmentId: change.rowId, tenantId });
      } else if (change.action === "replace" && change.rowId && payload) {
        await replaceAssignment.mutateAsync({ create: payload, deleteId: change.rowId });
      } else if (change.action === "create" && payload) {
        await createAssignments.mutateAsync([payload]);
      } else {
        throw new Error("Change cannot be applied");
      }
      setChangeStatus(entryId, change.key, "applied");
    } catch {
      setChangeStatus(entryId, change.key, "failed");
    }
  };

  const applyAll = async (items: PageChange[]) => {
    for (const item of items.filter(isOpen)) {
      await apply(item.entryId, item.change);
    }
  };

  const skip = (entryId: number, change: ChangeWithStatus) =>
    setChangeStatus(entryId, change.key, "skipped");

  const skipAll = (items: PageChange[]) =>
    items.filter(isOpen).forEach(({ entryId, change }) => skip(entryId, change));

  return {
    apply,
    applyAll,
    skip,
    skipAll,
    countOpen: (items: PageChange[]) => items.filter(isOpen).length,
  };
}
